import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { Input, Label } from "@/components/ui/Input";
import type { SortDirection } from "@/components/ui/SortableTableHeader";
import { CardTitleWithHelp, HoverHintTrigger, PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { BatchDetailLink } from "@/components/hub/HubEntityDetailLink";
import { ApiError } from "@/lib/api";
import { useManufacturingScopeHubWrite } from "@/lib/useHubModuleWrite";
import { cn, EM_DASH } from "@/lib/utils";
import { useHubToast } from "@/components/ui/HubToast";
import { playLaborIdleWarningSound, playPairFailureSound, playPairSuccessSound } from "@/lib/floorStationPairAudio";
import {
  clearLaserMarkSessionJobId,
  createLaserMarkJob,
  fetchActiveLaserMarkJob,
  fetchLaserMarkPreview,
  fetchLaserMarkQueue,
  LASER_MARK_ACTIVE_JOB_QUERY_KEY,
  LASER_MARK_QUERY_KEY,
  laserMarkLineKey,
  laserMarkSkuMarksConfiguration,
  markLaserMarkBurnCompleted,
  persistLaserMarkSessionJobId,
  reportLaserMarkFailedBurn,
  resendLaserMarkJob,
  voidLaserMarkJob,
  type LaserMarkJobResponse,
  type LaserMarkPreviewResponse,
  type LaserMarkQueueItem,
} from "./laserMarkApi";

const PAGE_HELP =
  "Reserve device serials and load the matching LightBurn file from the pre-configuration queue. Hub picks the FI-3C, FI-5A, or FP-OC rotary master from the selected line. FP-OC marks the configuration SKU. Operators swap the rotary chuck for FI-5A and FP-OC, load the fixture, and Send. Serials are stored in the existing label printed serial table so configuration cannot reuse them.";

const SEND_HELP =
  "Quantity defaults to a full fixture, or to remaining-to-mark when that is smaller. Send writes the CSV and loads the FI-3C, FI-5A, or FP-OC art for this line. Hub never starts the laser. Press the foot pedal when ready to mark.";

const PREVIEW_HELP =
  "Hub mock of the shared LightBurn variables: ECC200 Data Matrix, human serial, and configuration SKU. It is not a screenshot of the .lbrn artwork. Serials shown here are the next numbers that Send will reserve; they are not reserved until you click Send.";

type SortKey = "batchId" | "sku" | "remainingToMark" | "mfrShipByDate" | "fixtureFamily";

const LIST_AUTO_REFRESH_MS = 60_000;

function fixtureLabel(family: LaserMarkQueueItem["fixtureFamily"]): string {
  return family === "fi3c" ? "FI-3C (14)" : "FI-5A (1)";
}

function formatDate(value: string | null): string {
  if (value == null || value.trim() === "") return EM_DASH;
  return value.slice(0, 10);
}

function lightBurnMessage(result: LaserMarkJobResponse): string {
  if (result.lightBurn.ok === false) {
    return `Serials reserved. LightBurn send failed: ${result.lightBurn.error}`;
  }
  return result.lightBurn.detail ?? "Serials reserved and LightBurn was updated.";
}

function compareShipBy(left: string | null, right: string | null, direction: number): number {
  const leftDate = left?.trim() ?? "";
  const rightDate = right?.trim() ?? "";
  if (leftDate === "" && rightDate === "") return 0;
  if (leftDate === "") return 1;
  if (rightDate === "") return -1;
  return leftDate.localeCompare(rightDate) * direction;
}

export function LaserMarkPage() {
  const writesAllowed = useManufacturingScopeHubWrite();
  const { showToast } = useHubToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("mfrShipByDate");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [quantityInput, setQuantityInput] = useState("");
  const [lastJob, setLastJob] = useState<LaserMarkJobResponse | null>(null);
  const [burnPhase, setBurnPhase] = useState<"idle" | "armed" | "burning" | "completed">("idle");
  const [failedSerial, setFailedSerial] = useState("");
  const [reportedFailedSerials, setReportedFailedSerials] = useState<
    Array<{ serial: string; qualityIncidentId: number }>
  >([]);
  const [failedModalOpen, setFailedModalOpen] = useState(false);
  const [previewSlot, setPreviewSlot] = useState(1);
  const [monitoringOpen, setMonitoringOpen] = useState(false);

  const activeJobQuery = useQuery({
    queryKey: LASER_MARK_ACTIVE_JOB_QUERY_KEY,
    queryFn: fetchActiveLaserMarkJob,
  });

  const queueQuery = useQuery({
    queryKey: LASER_MARK_QUERY_KEY,
    queryFn: fetchLaserMarkQueue,
    refetchInterval: LIST_AUTO_REFRESH_MS,
  });

  const items = queueQuery.data?.items ?? [];
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === "") return items;
    return items.filter((item) =>
      [item.sku, item.salesOrderNumber ?? "", String(item.batchId ?? ""), fixtureLabel(item.fixtureFamily)]
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [items, search]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((left, right) => {
      const direction = sortDir === "asc" ? 1 : -1;
      if (sortKey === "remainingToMark") {
        return (left.remainingToMark - right.remainingToMark) * direction;
      }
      if (sortKey === "fixtureFamily") {
        return left.fixtureFamily.localeCompare(right.fixtureFamily) * direction;
      }
      if (sortKey === "sku") {
        return left.sku.localeCompare(right.sku) * direction;
      }
      if (sortKey === "batchId") {
        return ((left.batchId ?? 0) - (right.batchId ?? 0)) * direction;
      }
      return compareShipBy(left.mfrShipByDate, right.mfrShipByDate, direction);
    });
    return copy;
  }, [filtered, sortDir, sortKey]);

  const selected = sorted.find((item) => laserMarkLineKey(item) === selectedKey) ?? sorted[0] ?? null;

  useEffect(() => {
    if (selected == null) return;
    setSelectedKey(laserMarkLineKey(selected));
    setQuantityInput(String(selected.defaultMarkQuantity));
    setPreviewSlot(1);
  }, [selected?.batchItemId, selected?.alternateOrderId, selected?.defaultMarkQuantity]);

  useEffect(() => {
    if (lastJob != null) return;
    const hydrated = activeJobQuery.data;
    if (hydrated == null) return;
    setLastJob(hydrated);
    const completed = hydrated.burnCompletedAt != null && hydrated.burnCompletedAt !== "";
    setBurnPhase(completed ? "completed" : "armed");
    setMonitoringOpen(!completed && hydrated.lightBurn.ok !== false);
  }, [activeJobQuery.data, lastJob]);

  function toggleSort(key: SortKey): void {
    if (sortKey === key) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "remainingToMark" ? "desc" : "asc");
  }

  const sendMutation = useMutation({
    mutationFn: async () => {
      if (selected == null) throw new Error("Select a queue line first.");
      const quantity = Number.parseInt(quantityInput, 10);
      return await createLaserMarkJob({
        kind: selected.kind,
        batchItemId: selected.batchItemId,
        alternateOrderId: selected.alternateOrderId,
        quantity,
      });
    },
    onSuccess: async (result) => {
      setLastJob(result);
      persistLaserMarkSessionJobId(result.jobId);
      if (result.lightBurn.ok === false) {
        playLaborIdleWarningSound();
        setBurnPhase("idle");
        setMonitoringOpen(true);
      } else {
        playPairSuccessSound();
        setBurnPhase("armed");
        setMonitoringOpen(true);
      }
      showToast({
        tone: result.lightBurn.ok === false ? "info" : "success",
        message: `Reserved ${String(result.quantity)} serial${result.quantity === 1 ? "" : "s"}. ${lightBurnMessage(result)}`,
      });
      await queryClient.invalidateQueries({ queryKey: LASER_MARK_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: LASER_MARK_ACTIVE_JOB_QUERY_KEY });
    },
    onError: (error) => {
      const message = error instanceof ApiError ? error.message : error instanceof Error ? error.message : String(error);
      playPairFailureSound();
      showToast({ tone: "error", message });
    },
  });

  const resendMutation = useMutation({
    mutationFn: async () => {
      if (lastJob == null) throw new Error("No job to resend.");
      return await resendLaserMarkJob({ jobId: lastJob.jobId });
    },
    onSuccess: (result) => {
      setLastJob(result);
      persistLaserMarkSessionJobId(result.jobId);
      if (result.lightBurn.ok === false) {
        playLaborIdleWarningSound();
        setBurnPhase("idle");
        setMonitoringOpen(true);
      } else {
        playPairSuccessSound();
        setBurnPhase("armed");
        setMonitoringOpen(true);
      }
      showToast({
        tone: result.lightBurn.ok === false ? "info" : "success",
        message: lightBurnMessage(result),
      });
      void queryClient.invalidateQueries({ queryKey: LASER_MARK_ACTIVE_JOB_QUERY_KEY });
    },
    onError: (error) => {
      const message = error instanceof ApiError ? error.message : error instanceof Error ? error.message : String(error);
      playPairFailureSound();
      showToast({ tone: "error", message });
    },
  });

  const completeBurnMutation = useMutation({
    mutationFn: async () => {
      if (lastJob == null) throw new Error("No active laser-mark job.");
      return await markLaserMarkBurnCompleted(lastJob.jobId);
    },
    onSuccess: async () => {
      playPairSuccessSound();
      setBurnPhase("completed");
      setMonitoringOpen(false);
      setLastJob((current) =>
        current == null ? null : { ...current, burnCompletedAt: new Date().toISOString() },
      );
      clearLaserMarkSessionJobId();
      showToast({ tone: "success", message: "Burn recorded as complete." });
      await queryClient.invalidateQueries({ queryKey: LASER_MARK_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: LASER_MARK_ACTIVE_JOB_QUERY_KEY });
    },
    onError: (error) => {
      const message = error instanceof ApiError ? error.message : error instanceof Error ? error.message : String(error);
      playPairFailureSound();
      showToast({ tone: "error", message });
    },
  });

  const voidJobMutation = useMutation({
    mutationFn: async () => {
      if (lastJob == null) throw new Error("No active laser-mark job.");
      return await voidLaserMarkJob(lastJob.jobId);
    },
    onSuccess: async (result) => {
      playPairSuccessSound();
      setMonitoringOpen(false);
      setBurnPhase("idle");
      setLastJob(null);
      clearLaserMarkSessionJobId();
      const serialText = result.serials.length > 0 ? ` (${result.serials.join(", ")})` : "";
      showToast({
        tone: "info",
        message: `Cancelled job ${String(result.jobId)} and voided reserved serials${serialText}.`,
      });
      await queryClient.invalidateQueries({ queryKey: LASER_MARK_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: LASER_MARK_ACTIVE_JOB_QUERY_KEY });
    },
    onError: (error) => {
      const message = error instanceof ApiError ? error.message : error instanceof Error ? error.message : String(error);
      playPairFailureSound();
      showToast({ tone: "error", message });
    },
  });

  const failedBurnMutation = useMutation({
    mutationFn: async () => reportLaserMarkFailedBurn({ serial: failedSerial.trim() }),
    onSuccess: async (result) => {
      playPairSuccessSound();
      setFailedSerial("");
      setReportedFailedSerials((current) => [
        { serial: result.serial, qualityIncidentId: result.qualityIncidentId },
        ...current,
      ]);
      showToast({
        tone: "success",
        message: `NCR ${String(result.qualityIncidentId)} opened for ${result.serial}. Enter another serial or click Done.`,
      });
      await queryClient.invalidateQueries({ queryKey: LASER_MARK_QUERY_KEY });
    },
    onError: (error) => {
      const message = error instanceof ApiError ? error.message : error instanceof Error ? error.message : String(error);
      playPairFailureSound();
      showToast({ tone: "error", message });
    },
  });

  const maxQuantity = selected == null ? 0 : Math.min(selected.remainingToMark, selected.fixtureCapacity);
  const parsedQuantity = Number.parseInt(quantityInput, 10);
  const previewReady =
    selected != null && Number.isInteger(parsedQuantity) && parsedQuantity >= 1 && parsedQuantity <= maxQuantity;
  const previewQuery = useQuery({
    queryKey: [
      ...LASER_MARK_QUERY_KEY,
      "preview",
      selected == null ? null : laserMarkLineKey(selected),
      parsedQuantity,
      previewSlot,
    ],
    queryFn: () =>
      fetchLaserMarkPreview({
        kind: selected!.kind,
        batchItemId: selected!.batchItemId,
        alternateOrderId: selected!.alternateOrderId,
        quantity: parsedQuantity,
        slotIndex: previewSlot,
      }),
    enabled: previewReady,
  });
  const sendDisabled =
    !writesAllowed ||
    selected == null ||
    !Number.isInteger(parsedQuantity) ||
    parsedQuantity < 1 ||
    parsedQuantity > maxQuantity ||
    sendMutation.isPending;

  const readOnlyReason = "You have read-only access to Manufacturing.";

  const queueColumns = useMemo((): HubDataTableColumn<LaserMarkQueueItem, SortKey>[] => {
    return [
      {
        id: "batchId",
        header: "Batch",
        sortKey: "batchId",
        sortValue: (item) => item.batchId ?? -1,
        cell: (item) =>
          item.batchId != null ? <BatchDetailLink batchId={item.batchId} /> : EM_DASH,
      },
      {
        id: "fixtureFamily",
        header: "FI-3C / FI-5A",
        sortKey: "fixtureFamily",
        sortValue: (item) => item.fixtureFamily,
        cell: (item) => fixtureLabel(item.fixtureFamily),
      },
      {
        id: "sku",
        header: "SKU",
        sortKey: "sku",
        sortValue: (item) => item.sku,
        cell: (item) => <span className="font-medium">{item.sku}</span>,
      },
      {
        id: "remainingToMark",
        header: "Qty remaining",
        sortKey: "remainingToMark",
        align: "right",
        sortValue: (item) => item.remainingToMark,
        cell: (item) => item.remainingToMark,
      },
      {
        id: "mfrShipByDate",
        header: "Ship by",
        sortKey: "mfrShipByDate",
        overflow: "nowrap",
        sortValue: (item) => item.mfrShipByDate ?? "",
        cell: (item) => formatDate(item.mfrShipByDate),
      },
    ];
  }, []);

  const filtersActive = search.trim() !== "";

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <PageTitleWithHelp hoverHint={PAGE_HELP}>Laser mark</PageTitleWithHelp>
        <HoverHintTrigger
          trigger={
            <Button
              type="button"
              variant="secondary"
              disabled={queueQuery.isFetching}
              onClick={() => void queueQuery.refetch()}
            >
              {queueQuery.isFetching ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <RefreshCw className="h-4 w-4" aria-hidden />
              )}
              Refresh
            </Button>
          }
          align="center"
        >
          Queue remaining-to-mark subtracts serials already reserved and not yet configured.
        </HoverHintTrigger>
      </header>

      <Card>
        <CardHeader>
          <CardTitleWithHelp hoverHint="Search still matches SKU, sales order, and batch. Sales order is not a table column.">
            Filters
          </CardTitleWithHelp>
        </CardHeader>
        <CardBody>
          <Label htmlFor="laser-mark-search">Search</Label>
          <Input
            id="laser-mark-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="SKU, sales order, or batch"
          />
        </CardBody>
      </Card>

      <div className="grid min-h-[28rem] gap-4 lg:grid-cols-[minmax(28rem,1.35fr)_minmax(22rem,1fr)] lg:items-stretch">
        <HubDataTable
          title={
            <CardTitleWithHelp hoverHint="Same pre-configuration source as Configuration. Only FI-3C and FI-5A family SKUs appear. Default sort is ship-by date, oldest first. Rows with no ship-by date sort last.">
              Pre-configuration queue
            </CardTitleWithHelp>
          }
          columns={queueColumns}
          rows={sorted}
          rowKey={(item) => laserMarkLineKey(item)}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={toggleSort}
          onRowClick={(item) => setSelectedKey(laserMarkLineKey(item))}
          rowClassName={(item) =>
            selected != null && laserMarkLineKey(selected) === laserMarkLineKey(item) ? "bg-sky-50" : undefined
          }
          loading={queueQuery.isLoading}
          loadingMessage="Loading queue…"
          error={queueQuery.isError ? (queueQuery.error as Error).message : null}
          empty="No FI-3C or FI-5A lines are waiting to mark."
          isFiltered={filtersActive}
          filteredEmpty="No rows match these filters."
          tableClassName="min-w-[40rem]"
        />

        <HubViewportCard>
          <CardHeader>
            <CardTitleWithHelp hoverHint={SEND_HELP}>Queue to send</CardTitleWithHelp>
            {selected != null ? <Badge>{fixtureLabel(selected.fixtureFamily)}</Badge> : null}
          </CardHeader>
          <CardBody className="min-h-0 space-y-4 overflow-auto">
            {selected == null ? (
              <p className="text-sm text-slate-500">Select a queue line.</p>
            ) : (
              <>
                <p className="text-sm text-slate-600">
                  {selected.sku} · remaining to mark {String(selected.remainingToMark)} · fixture holds{" "}
                  {String(selected.fixtureCapacity)} · labels printed {String(selected.reservedUnconfiguredCount)}
                </p>
                <div className="w-40">
                  <Label htmlFor="laser-mark-qty">Quantity this load</Label>
                  <Input
                    id="laser-mark-qty"
                    inputMode="numeric"
                    value={quantityInput}
                    onChange={(event) => setQuantityInput(event.target.value)}
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Enter 1 through {String(maxQuantity)} so a partial fixture still gets unique reserved serials.
                </p>
                <LaserMarkArtPreview
                  preview={previewQuery.data ?? null}
                  loading={previewQuery.isFetching}
                  errorMessage={
                    previewQuery.isError
                      ? previewQuery.error instanceof ApiError
                        ? previewQuery.error.message
                        : previewQuery.error instanceof Error
                          ? previewQuery.error.message
                          : String(previewQuery.error)
                      : null
                  }
                  onSelectSlot={setPreviewSlot}
                />
                <div className="flex flex-wrap gap-2">
                  <Button type="button" disabled={sendDisabled} onClick={() => sendMutation.mutate()}>
                    {sendMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                    Send
                  </Button>
                  <ReadOnlyHoverLayer active={!writesAllowed} hoverHint={readOnlyReason} layout="inline">
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={!writesAllowed}
                      onClick={() => {
                        setReportedFailedSerials([]);
                        setFailedSerial("");
                        setFailedModalOpen(true);
                      }}
                    >
                      Report failed burn
                    </Button>
                  </ReadOnlyHoverLayer>
                </div>
                {lastJob != null ? (
                  <div className="flex flex-wrap items-center gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                    <span className="font-mono">
                      Last job {String(lastJob.jobId)}: {lastJob.serials.join(", ")}
                      {burnPhase === "completed" ? " · Burn finished" : " · Open"}
                    </span>
                    <ReadOnlyHoverLayer active={!writesAllowed} hoverHint={readOnlyReason} layout="inline">
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={!writesAllowed || resendMutation.isPending}
                        onClick={() => resendMutation.mutate()}
                      >
                        {resendMutation.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                        ) : null}
                        Didn’t burn yet? Reload LightBurn
                      </Button>
                    </ReadOnlyHoverLayer>
                  </div>
                ) : null}
              </>
            )}
          </CardBody>
        </HubViewportCard>
      </div>
      {monitoringOpen && lastJob != null ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="laser-mark-monitoring-title"
        >
          <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-4 shadow-lg">
            <h2 id="laser-mark-monitoring-title" className="text-lg font-semibold text-slate-900">
              {lastJob.lightBurn.ok === false ? "LightBurn did not load" : "Sent to LightBurn"}
            </h2>
            <p className="mt-1 font-mono text-xs text-slate-600">
              Job {String(lastJob.jobId)} · Serials: {lastJob.serials.join(", ")}
            </p>
            {lastJob.lightBurn.ok === false ? (
              <p className="mt-2 text-sm text-rose-700">
                {lastJob.lightBurn.error ?? "Could not reach LightBurn on the laser PC."}
              </p>
            ) : (
              <p className="mt-2 text-sm text-slate-600">
                Frame and align on the rotary if needed, then trigger the laser. Click{" "}
                <span className="font-medium">Burn finished</span> after the mark completes, or{" "}
                <span className="font-medium">Cancel &amp; void serials</span> if you have not burned yet.
              </p>
            )}
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={voidJobMutation.isPending || completeBurnMutation.isPending || resendMutation.isPending}
                onClick={() => voidJobMutation.mutate()}
              >
                {voidJobMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                Cancel &amp; void serials
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={resendMutation.isPending || voidJobMutation.isPending || completeBurnMutation.isPending}
                onClick={() => resendMutation.mutate()}
              >
                {resendMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                Reload LightBurn
              </Button>
              <Button
                type="button"
                disabled={completeBurnMutation.isPending || voidJobMutation.isPending || resendMutation.isPending}
                onClick={() => completeBurnMutation.mutate()}
              >
                {completeBurnMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                Burn finished
              </Button>
            </div>
          </div>
        </div>
      ) : null}
      {failedModalOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="laser-mark-failed-burn-title"
        >
          <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-4 shadow-lg">
            <h2 id="laser-mark-failed-burn-title" className="text-lg font-semibold text-slate-900">
              Report failed burn
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Enter each serial whose physical mark failed. Hub opens an NCR, keeps the serial allocated so it is not
              reused, and does not count it toward marked quantity.
            </p>
            <form
              className="mt-3 space-y-3"
              onSubmit={(event) => {
                event.preventDefault();
                if (failedSerial.trim() === "" || failedBurnMutation.isPending) return;
                failedBurnMutation.mutate();
              }}
            >
              <div>
                <Label htmlFor="laser-mark-failed-serial">Serial number</Label>
                <Input
                  id="laser-mark-failed-serial"
                  value={failedSerial}
                  onChange={(event) => setFailedSerial(event.target.value)}
                  placeholder="For example A01P2"
                  autoComplete="off"
                  autoFocus
                />
              </div>
              {reportedFailedSerials.length > 0 ? (
                <div className="rounded border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700">
                  <p className="font-medium text-slate-900">Reported this session</p>
                  <ul className="mt-1 list-inside list-disc space-y-0.5 font-mono">
                    {reportedFailedSerials.map((entry, idx) => (
                      <li key={`${entry.serial}-${String(idx)}`}>
                        {entry.serial} (NCR {String(entry.qualityIncidentId)})
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={failedBurnMutation.isPending}
                  onClick={() => {
                    setFailedModalOpen(false);
                    setFailedSerial("");
                  }}
                >
                  Done
                </Button>
                <Button type="submit" disabled={failedSerial.trim() === "" || failedBurnMutation.isPending}>
                  {failedBurnMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                  Submit serial
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function LaserMarkArtPreview(props: {
  preview: LaserMarkPreviewResponse | null;
  loading: boolean;
  errorMessage: string | null;
  onSelectSlot: (slotIndex: number) => void;
}): JSX.Element {
  return (
    <div className="space-y-3 rounded border border-slate-200 bg-slate-50 p-3">
      <CardTitleWithHelp hoverHint={PREVIEW_HELP}>Mark preview</CardTitleWithHelp>
      {props.loading && props.preview == null ? <p className="text-sm text-slate-500">Loading preview…</p> : null}
      {props.errorMessage != null ? <p className="text-sm text-rose-700">{props.errorMessage}</p> : null}
      {props.preview != null ? (
        <LaserMarkArtPreviewBody preview={props.preview} onSelectSlot={props.onSelectSlot} />
      ) : null}
    </div>
  );
}

function LaserMarkArtPreviewBody(props: {
  preview: LaserMarkPreviewResponse;
  onSelectSlot: (slotIndex: number) => void;
}): JSX.Element {
  const preview = props.preview;
  return (
    <div className="grid gap-4 lg:grid-cols-[auto_minmax(0,1fr)] lg:items-start">
      <DatamatrixModulesSvg modules={preview.datamatrixModules} serial={preview.serial} />
      <div className="space-y-2 text-sm">
        <p>
          <span className="text-slate-500">Configuration</span>
          <br />
          <span className="font-medium">
            {preview.fixtureFamily === "fi5a" && !laserMarkSkuMarksConfiguration(preview.sku)
              ? `${preview.configuration} (not marked on shell)`
              : preview.configuration}
          </span>
        </p>
        <p>
          <span className="text-slate-500">Human serial (slot {String(preview.slotIndex)})</span>
          <br />
          <span className="font-mono">{preview.serial}</span>
        </p>
        <p className="text-xs text-slate-500">
          {preview.fixtureFamily === "fi3c" ? "FI-3C 14-up nest" : "FI-5A single nest"} · this load{" "}
          {String(preview.quantity)} of {String(preview.fixtureCapacity)}. Click a slot to preview that unit. Serials
          are not reserved until Send.
        </p>
        <div
          className={cn(
            "grid gap-1",
            preview.fixtureFamily === "fi3c" ? "grid-cols-7 max-w-md" : "grid-cols-1 max-w-[3rem]",
          )}
        >
          {Array.from({ length: preview.fixtureCapacity }, (_, index) => {
            const slotIndex = index + 1;
            const loaded = slotIndex <= preview.quantity;
            const active = loaded && slotIndex === preview.slotIndex;
            return (
              <button
                key={slotIndex}
                type="button"
                disabled={!loaded}
                className={cn(
                  "h-8 rounded border text-xs tabular-nums",
                  !loaded
                    ? "border-slate-200 bg-white text-slate-300"
                    : active
                      ? "border-sky-600 bg-sky-100 text-sky-900"
                      : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100",
                )}
                onClick={() => props.onSelectSlot(slotIndex)}
              >
                {slotIndex}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DatamatrixModulesSvg(props: { modules: boolean[][]; serial: string }): JSX.Element {
  const size = props.modules.length;
  return (
    <svg
      role="img"
      aria-label={`Data Matrix for serial ${props.serial}`}
      viewBox={`0 0 ${String(size)} ${String(size)}`}
      className="h-44 w-44 border border-slate-300 bg-white p-1"
      shapeRendering="crispEdges"
    >
      {props.modules.flatMap((row, y) =>
        row.flatMap((dark, x) =>
          dark
            ? [
                <rect
                  key={`${String(x)}-${String(y)}`}
                  x={x}
                  y={y}
                  width={1}
                  height={1}
                  fill="#111827"
                />,
              ]
            : [],
        ),
      )}
    </svg>
  );
}
